// The multi-object scene model for Model mode: the list of movable voxel
// objects, the "active" one (wraps the primary grid + its mesher at the
// identity transform), and the room-object resolution used by surface draws.
// Owned here so the draw/pick pipeline (main's pick loop, the sketch tool) reads
// one source of truth. The per-controller pick→object routing state
// (lastPickObject / gestureObjectId) lives here too; the input layers write it
// through the returned array / setter.

import * as THREE from "three";
import { grid } from "../core/edit-core/grid";
import { uiState } from "../ui/state";
import { VoxelGrid } from "./voxel-grid";
import { type SceneObject, createSceneObject, makeSceneObject } from "./scene-object";
import { VoxelMesher, type PaletteLookup } from "../view/voxel-mesh";

// Sentinel pick-target id: a room-surface hit (not a specific SceneObject).
export const ROOM_TARGET = -2;

export interface SceneObjectsDeps {
  palette: PaletteLookup;
  gridGroup: THREE.Group; // objects ride the canvas transform
  // Wakes the render loop when the meshing worker delivers geometry (it arrives
  // outside any input event, so the idle gate would otherwise miss it).
  onMeshApplied?: () => void;
}

export interface SceneObjectsApi {
  activeObject: SceneObject; // the object modeling edits by default
  sceneObjects: SceneObject[]; // live list (render loop syncs each mesher)
  // Per-controller pick result: a SceneObject id, or ROOM_TARGET for a room
  // surface hit. The pick loop writes this array by index; the sketch tool reads
  // it. Drives draw routing + cursor frame.
  lastPickObject: number[];
  surfaceFallbackGrid: VoxelGrid; // occupancy stand-in when no room object exists
  objectById: (id: number) => SceneObject;
  gestureObject: () => SceneObject; // the object the active draw gesture targets
  gestureActive: () => boolean; // a draw gesture is writing into an object
  setGestureObject: (id: number) => void; // -1 clears (no gesture active)
  isRoomAligned: (g: THREE.Object3D) => boolean; // identity transform → room geometry
  findRoomObject: () => SceneObject | null;
  getOrCreateRoomObject: () => SceneObject;
  setAllGridVisible: (v: boolean) => void; // mirror the toggle onto every object
  setAllEdgesVisible: (v: boolean) => void;
  // Zoom-fade grid + edge line opacity on every object (see VoxelMesher.setLineFade).
  setAllLineFade: (gridFade: number, edgeFade: number) => void;
}

export function installSceneObjects(deps: SceneObjectsDeps): SceneObjectsApi {
  const { palette, gridGroup } = deps;

  // The active object's mesher runs off-thread: its heavy greedy-mesh loops run
  // in a Worker so big edits (Fill / large box) don't stall the render thread.
  // Placement-mode objects (added later) stay synchronous — they mesh once when
  // placed, not interactively. Falls back to synchronous if Workers are absent.
  let meshWorker: Worker | null = null;
  try {
    meshWorker = new Worker(new URL("../view/mesher.worker.ts", import.meta.url), {
      type: "module",
    });
  } catch {
    meshWorker = null;
  }

  // The primary mesher wraps the owned grid; the active object pairs them at the
  // identity transform (a single object until placement mode adds more).
  const mesher = new VoxelMesher(palette, meshWorker, deps.onMeshApplied ?? null);
  const activeObject: SceneObject = makeSceneObject(grid, mesher);
  const sceneObjects: SceneObject[] = [activeObject];
  gridGroup.add(activeObject.group);
  // Initial mesher visualization state from uiState (so a saved preference would
  // survive reloads if/when persistence lands).
  mesher.setGridVisible(uiState.gridLinesVisible);
  mesher.setEdgesVisible(uiState.edgesVisible);

  const lastPickObject: number[] = [ROOM_TARGET, ROOM_TARGET];
  // The object a draw gesture is currently writing into (set on press, cleared
  // on release). -1 when no gesture is active.
  let gestureObjectId = -1;

  function isRoomAligned(g: THREE.Object3D): boolean {
    return (
      g.position.lengthSq() < 1e-8 && Math.abs(Math.abs(g.quaternion.w) - 1) < 1e-6
    );
  }
  function objectById(id: number): SceneObject {
    for (const o of sceneObjects) if (o.id === id) return o;
    return activeObject;
  }
  // The object the active draw gesture targets (its grid/frame/bounds feed the
  // per-frame drag handlers). Falls back to the active object.
  function gestureObject(): SceneObject {
    return gestureObjectId >= 0 ? objectById(gestureObjectId) : activeObject;
  }
  // Find an existing room-aligned (identity) object to draw room geometry into;
  // null if every object has been moved/rotated.
  function findRoomObject(): SceneObject | null {
    for (const o of sceneObjects) if (isRoomAligned(o.group)) return o;
    return null;
  }
  // Room-aligned object for surface draws, creating a fresh one if all existing
  // objects have been placed (moved off identity).
  function getOrCreateRoomObject(): SceneObject {
    const existing = findRoomObject();
    if (existing) return existing;
    const obj = createSceneObject(palette);
    obj.mesher.setGridVisible(uiState.gridLinesVisible);
    obj.mesher.setEdgesVisible(uiState.edgesVisible);
    gridGroup.add(obj.group);
    sceneObjects.push(obj);
    return obj;
  }

  // Empty grid for the surface raycast's occupancy test when no room object
  // exists yet.
  const surfaceFallbackGrid = new VoxelGrid();

  return {
    activeObject,
    sceneObjects,
    lastPickObject,
    surfaceFallbackGrid,
    objectById,
    gestureObject,
    gestureActive: () => gestureObjectId >= 0,
    setGestureObject: (id) => {
      gestureObjectId = id;
    },
    isRoomAligned,
    findRoomObject,
    getOrCreateRoomObject,
    setAllGridVisible: (v) => {
      for (const o of sceneObjects) o.mesher.setGridVisible(v);
    },
    setAllEdgesVisible: (v) => {
      for (const o of sceneObjects) o.mesher.setEdgesVisible(v);
    },
    setAllLineFade: (gridFade, edgeFade) => {
      for (const o of sceneObjects) o.mesher.setLineFade(gridFade, edgeFade);
    },
  };
}
